/**
 * Which CER portal upload input each captured photo belongs in.
 *
 * The portal exposes 13 distinct file inputs across the wizard (some per-row,
 * one per-clause). The field PWA captures against 40 prompt keys plus
 * `clause:<checklistItemId>` evidence shots. This module is the join.
 *
 * WHY IT LIVES IN `shared`
 * ------------------------
 * Two consumers need the same answer:
 *   - the backend, deciding which photo refs to put on the portal payload
 *     (`qa/assemble-payload.ts` currently ships only the mandatory hardware
 *     slots — "the portal upload only needs the spec/label shots");
 *   - the extension, routing each ref to a DOM input.
 * Splitting that decision across two packages is how they drift apart.
 *
 * THE JOIN AXIS
 * -------------
 * A prompt's `subject` ("PV array", "Inverter", "Solar panels", "Isolators",
 * "Switchboard", "Battery system") lines up almost exactly with the portal's
 * upload targets, which is unsurprising — both follow the same physical
 * inspection structure. The mapping below is keyed by prompt key rather than
 * derived from `subject`, because the exceptions matter more than the rule and
 * an explicit table is auditable. See `docs/CER_PORTAL_BATTERY.md` §3.
 *
 * BATCH UPLOAD IS SUPPORTED
 * -------------------------
 * Every `input[type=file]` on a Section 5 submission is `multiple=true` —
 * 126 of 126 across Coversheet (1), Hardware (5), Battery Checklist (119) and
 * Assessment (1), measured on BTY6985134 / submission 76751, build 3.9.54.0,
 * 2026-08-11. So the targets below that map to more than one prompt (the
 * inverter set is six shots) can be filled in a single `DataTransfer`.
 *
 * The five PV-only targets — solar array, module serial, module label and the
 * three DC-isolator phases — are *inferred* from that consistency rather than
 * measured; they need a PVD submission to confirm.
 *
 * Two things the same inventory established, both of which belong to the
 * uploader rather than to this table but are recorded here so they travel with
 * the mapping:
 *
 *   - **`accept` is unset on every input.** Nothing is rejected client-side on
 *     type, so a wrong-format file lands silently. Validation is ours.
 *   - **Never gate an upload on visibility.** All 119 clause inputs sit inside
 *     a collapsed `div#show_files_checklist_<n>` (`display:none`), so
 *     `offsetParent === null` for every one of them; a visibility check would
 *     refuse all clause evidence. The genuinely-wrong-to-write input — the
 *     Section 4 block on a Section 5 job — is indistinguishable by that test.
 *     Gate the battery block on the `Battery_System_Type` radio value instead.
 *     Assigning `input.files` works fine on a `display:none` input.
 */
/** Stable string form, for logging, grouping and test assertions. */
export function uploadTargetKey(target) {
    switch (target.kind) {
        case 'dc-isolator':
            return `dc-isolator:${target.phase}`;
        case 'checklist-clause':
            return `checklist-clause:${target.checklistItemId}`;
        case 'inverter-row':
            return target.index === undefined ? 'inverter-row' : `inverter-row:${target.index}`;
        default:
            return target.kind;
    }
}
/** `inverter-row:<index>:<prompt key>` files one battery inverter row's photo. */
const INVERTER_ROW_PROMPT = /^inverter-row:(\d+):(.+)$/;
/** Prompt key with a battery inverter-row prefix removed. */
export function canonicalPromptKey(promptKey) {
    const key = promptKey.trim();
    const indexed = INVERTER_ROW_PROMPT.exec(key);
    return indexed?.[2]?.trim() || key;
}
/** The `clause:<checklistItemId>` convention used by evidence shots. */
export const CLAUSE_PROMPT_PREFIX = 'clause:';
/**
 * Prompt key → upload target(s).
 *
 * Only prompts with a defensible portal destination appear. A prompt absent
 * from this table is still worth capturing — it goes in the report and the
 * Dropbox gallery — it simply has no portal input of its own.
 *
 * Absence means one of two things, and the difference matters: either a
 * documented "no home" (UNMAPPED_PROMPT_NOTES) or an unresolved gap
 * (PENDING_PORTAL_REVIEW). A completeness test forces every prompt into one of
 * the three buckets, so nothing falls out of the mapping unnoticed.
 */
const PROMPT_TO_TARGETS = Object.freeze({
    /* ── PV Hardware ─────────────────────────────────────────────────────── */
    'pv-array-overview': [{ kind: 'solar-array' }],
    'pv-array-pv-array-signage-additional-warning-signage': [{ kind: 'solar-array' }],
    'pv-inverter-far-back': [{ kind: 'inverter-row' }],
    'pv-inverter-left': [{ kind: 'inverter-row' }],
    'pv-inverter-right': [{ kind: 'inverter-row' }],
    'pv-inverter-model-details': [{ kind: 'inverter-row' }],
    'pv-inverter-serial': [{ kind: 'inverter-row' }],
    'pv-inverter-integrated-isolator': [{ kind: 'inverter-row' }],
    'pv-module-serial': [{ kind: 'module-serial' }],
    'pv-module-model-details': [{ kind: 'module-label' }],
    // The cleanest mapping on the whole portal: three inputs, three required
    // prompts, the same before/during/after vocabulary on both sides.
    'pv-dc-isolator-before': [{ kind: 'dc-isolator', phase: 'before' }],
    'pv-dc-isolator-during': [{ kind: 'dc-isolator', phase: 'during' }],
    'pv-dc-isolator-after': [{ kind: 'dc-isolator', phase: 'after' }],
    /* ── Battery Hardware ────────────────────────────────────────────────── */
    // `switchboard` is an addendum prompt — MANDATORY_HARDWARE_PHOTOS requires a
    // switchboard shot on both battery sections, though the May-2026 photo-list
    // docx omits it. Confirm with the technical head before an uploader depends
    // on it being present.
    switchboard: [{ kind: 'battery-switchboard' }],
    // The two stand-back label shots the portal's battery blocks exist to hold.
    // Both are mandatory on both battery sections.
    'battery-system-battery-system-label-and-serial-number': [
        { kind: 'battery-system-integrated' },
        { kind: 'battery-system-preassembled' },
    ],
    'battery-system-battery-modules-labels-and-serial-numbers': [
        { kind: 'battery-system-integrated' },
        { kind: 'battery-system-preassembled' },
    ],
    // "Battery system installation location (at distance / close-up)" is the only
    // prompt that evidences WHICH system type was installed, which is what
    // `BatterySystemTypeFiles` sits next to on the form.
    'battery-system-battery-system-installation-location-at-distance-close-up': [
        { kind: 'battery-system-type' },
    ],
    // Battery inverter shots share the PV inverter row inputs — the battery
    // Hardware page reuses `InverterList[i]`, just with a smaller field set.
    'inverter-inverter-label-and-serial-number': [{ kind: 'inverter-row' }],
    'inverter-inverter-showing-ac-and-dc-isolators': [{ kind: 'inverter-row' }],
    'inverter-inverter-mounting-screws-to-bracket': [{ kind: 'inverter-row' }],
    'inverter-inverter-frame-earthing-method': [{ kind: 'inverter-row' }],
    'inverter-inverter-measurement-to-objects': [{ kind: 'inverter-row' }],
    'inverter-inverter-shutdown-procedure': [{ kind: 'inverter-row' }],
});
/**
 * Shared reason for the shots closed out by the 2026-08-11 inventory. Written
 * once so fourteen entries can't drift into fourteen slightly different stories.
 */
const NO_HARDWARE_SLOT = 'No hardware upload input exists for this shot — a battery submission renders only the eight input kinds already mapped. It reaches the portal as clause evidence via a clause: ref, routed to whichever clause the inspector attached it to. Confirmed by full input inventory on BTY6985134 (build 3.9.54.0, 2026-08-11).';
/**
 * Prompts deliberately left without a portal target, and why. Exported so the
 * reason travels with the decision instead of living in a commit message.
 */
export const UNMAPPED_PROMPT_NOTES = Object.freeze({
    'pv-main-switch-inverter-supply': "PV's Hardware page has no switchboard upload input — only the battery one does. This shot's portal home is a clause evidence upload, if the QA has attached it to a clause.",
    'pv-ac-isolator-rating': 'No AC-isolator upload input exists; the portal only takes photos for DC isolators.',
    'isolators-ac-isolators-before-during-after': 'Same reason as pv-ac-isolator-rating — the portal has DC isolator inputs only. Reaches the portal as clause evidence if attached to a clause.',
    'general-close-up-photos-of-any-non-compliance-including-multi-glands': 'By definition non-compliance evidence — belongs on the clause it evidences, via a clause: ref, not a fixed hardware slot.',
    /* ── Closed 2026-08-11 by a complete upload-input inventory ────────────────
     *
     * These fourteen sat in PENDING_PORTAL_REVIEW because "clause evidence, not a
     * hardware slot" was only a guess. It is now an argument from exhaustion: a
     * battery submission renders exactly eight upload-input kinds (consent,
     * switchboard, battery-system-type, the two BESS/BS blocks, inverter row,
     * per-clause, assessment) and every one is accounted for above. There is no
     * remaining hardware slot for any of these shots to occupy.
     *
     * So they reach the portal the way the non-compliance prompt does — as
     * `clause:<checklistItemId>` evidence, routed at runtime to whichever clause
     * the inspector attached them to. That needs no static mapping, which is why
     * they belong here rather than in PROMPT_TO_TARGETS.
     *
     * Verified on BTY6985134 / submission 76751, portal build 3.9.54.0.
     * ───────────────────────────────────────────────────────────────────────── */
    'isolators-battery-isolator-signage': NO_HARDWARE_SLOT,
    'isolators-battery-load-breaker-disconnector-or-circuit-breaker-before-': NO_HARDWARE_SLOT,
    'isolators-dc-load-break-disconnectors': NO_HARDWARE_SLOT,
    'battery-system-battery-signage-on-hd-conduit-between-battery-and-inverter': NO_HARDWARE_SLOT,
    'battery-system-battery-system-and-bollard': NO_HARDWARE_SLOT,
    'battery-system-battery-frame-earthing-method': NO_HARDWARE_SLOT,
    'battery-system-battery-mounting-screws-to-brackets': NO_HARDWARE_SLOT,
    'battery-system-battery-measurements-to-objects-side-below-top': NO_HARDWARE_SLOT,
    'battery-system-battery-system-internal-cabling': NO_HARDWARE_SLOT,
    'battery-system-battery-sign-label': NO_HARDWARE_SLOT,
    'battery-system-battery-shutdown-procedure-signage': NO_HARDWARE_SLOT,
    'battery-system-battery-danger-risk-of-battery-explosion-signage': NO_HARDWARE_SLOT,
    'battery-system-battery-danger-toxic-fumes-signage': NO_HARDWARE_SLOT,
    'battery-system-battery-danger-arc-flash-hazard-signage-if-applicable': NO_HARDWARE_SLOT,
});
/**
 * Prompts with neither a portal target nor a documented reason for lacking one.
 *
 * **Currently empty — and that is the goal state, not an oversight.** The
 * fourteen entries this list once held were closed on 2026-08-11 by a complete
 * upload-input inventory; see the block comment in {@link UNMAPPED_PROMPT_NOTES}.
 *
 * Keep the list. It is a debt register, and the completeness test pins it
 * exactly, so a newly added prompt cannot quietly land in limbo. Resolving an
 * entry means mapping it in {@link PROMPT_TO_TARGETS} or explaining it in
 * {@link UNMAPPED_PROMPT_NOTES} — and deleting it from here. Adding a key here
 * to silence a failing test is the one thing this must never be used for.
 */
export const PENDING_PORTAL_REVIEW = Object.freeze([]);
/**
 * Where should this photo be uploaded?
 *
 * `clause:<checklistItemId>` refs always route to that clause's own upload
 * input. Everything else comes from the table above.
 *
 * Returns `[]` when the prompt has no portal home — the caller should skip it,
 * not invent a destination. A photo in the wrong portal slot is a compliance
 * record asserting the wrong evidence.
 */
export function uploadTargetsForPrompt(promptKey) {
    const key = promptKey.trim();
    const indexed = INVERTER_ROW_PROMPT.exec(key);
    if (indexed?.[1] && indexed[2]) {
        const index = Number(indexed[1]);
        return uploadTargetsForPrompt(indexed[2]).map((target) => target.kind === 'inverter-row' ? { kind: 'inverter-row', index } : target);
    }
    if (key === 'consent-signed-form')
        return [{ kind: 'consent' }];
    if (key.startsWith(CLAUSE_PROMPT_PREFIX)) {
        const checklistItemId = key.slice(CLAUSE_PROMPT_PREFIX.length).trim();
        if (checklistItemId.length === 0)
            return [];
        return [{ kind: 'checklist-clause', checklistItemId }];
    }
    return PROMPT_TO_TARGETS[key] ?? [];
}
/**
 * Narrow a multi-target prompt to the one that applies to this section.
 *
 * The battery label shots map to both the integrated (section 4) and
 * pre-assembled (section 5) blocks, because the portal renders whichever the
 * `Battery_System_Type` radio selects — only one exists on the page at a time.
 */
export function uploadTargetsForPromptInSection(promptKey, section) {
    const targets = uploadTargetsForPrompt(promptKey);
    return targets.filter((t) => {
        if (t.kind === 'battery-system-integrated')
            return section === 'battery-section-4-ac';
        if (t.kind === 'battery-system-preassembled')
            return section === 'battery-section-5-dc';
        return true;
    });
}
/**
 * Hardware-page slots that correspond to a mandatory-photo subject.
 *
 * The fill planner does not send battery clause photos here. Every battery
 * clause, Compliant included, uploads on its checklist row
 * (`UploadedChecklistFiles.FileBase`). This remains the structural map from
 * a subject to the Hardware inputs (switchboard, pre-assembled BESS,
 * integrated BESS).
 *
 * Keyed by `MandatoryPhoto.subject` because that is the vocabulary the
 * mandatory-photo register speaks. A subject absent here has no hardware
 * slot in this table. Multi-target entries follow the same rule as
 * {@link PROMPT_TO_TARGETS}' battery labels: the portal renders whichever
 * block the `Battery_System_Type` radio selects.
 */
export function hardwareFallbackTargets(subject) {
    switch (subject) {
        case 'Switchboard':
            return [{ kind: 'battery-switchboard' }];
        case 'Battery system':
            return [{ kind: 'battery-system-preassembled' }, { kind: 'battery-system-integrated' }];
        default:
            return [];
    }
}
/** Every prompt key with a portal destination. Exported for coverage tests. */
export const MAPPED_PROMPT_KEYS = Object.freeze(Object.keys(PROMPT_TO_TARGETS));
//# sourceMappingURL=portal-photo-targets.js.map